import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { email } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Text, Group, Button, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { useEmailStatus } from '../../../lib/email';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { CopyValue } from './components/CopyValue';
import { OwnDomainCard } from './components/OwnDomainCard';
import { emailErrorMessage } from './errors';
import './email.css';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Email, 4 October 2026.
 *
 * The business's address on the platform (customers write to it, replies come
 * back to it, and a mailbox can forward to it so its mail reaches the Inbox
 * too), the name and reply-to its email carries, the switch that sends a
 * WhatsApp service message by email when WhatsApp cannot deliver it, and
 * sending from the business's own domain.
 *
 * Everybody in the team can read it; only the owner and admins can change it
 * (the service refuses an agent with ADMIN_ROLE_REQUIRED), so for an agent
 * every control is shown read-only with a line saying why. The page does not
 * exist while the platform cannot send email: an old link lands on the hub.
 */
export default function SettingsEmailPage() {
  const t = useT();
  const qc = useQueryClient();
  const emailStatus = useEmailStatus();
  const canEdit = useIsOrgAdmin();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['email-settings'],
    queryFn: () => email.getSettings(),
    enabled: emailStatus.available
  });
  const settings = data?.data ?? null;

  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [replyError, setReplyError] = useState('');
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    if (!settings) return;
    setSenderName(settings.sender_name ?? '');
    setReplyTo(settings.reply_to ?? '');
    setFallback(settings.fallback_enabled);
  }, [settings]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['email-settings'] });
    // The status carries the address, the reply-to and the fallback flag, and other pages read it.
    void qc.invalidateQueries({ queryKey: ['email-status'] });
  };

  const saveSender = useMutation({
    mutationFn: () => email.updateSettings({ sender_name: senderName.trim(), reply_to: replyTo.trim() }),
    onSuccess: () => { invalidate(); notifySuccess(t('settings.email.sender.saved'), t('settings.nav.email')); },
    onError: (e) => notifyError(emailErrorMessage(t, e), t('settings.nav.email'))
  });

  // The switch saves itself and reverts visibly if the write fails, the same as SMS.
  const saveFallback = useMutation({
    mutationFn: (next: boolean) => email.updateSettings({ fallback_enabled: next }),
    onMutate: (next) => {
      const previous = fallback;
      setFallback(next);
      return { previous };
    },
    onError: (e, _next, ctx) => {
      if (ctx) setFallback(ctx.previous);
      notifyError(emailErrorMessage(t, e), t('settings.nav.email'));
    },
    onSettled: invalidate
  });

  function submitSender() {
    const reply = replyTo.trim();
    if (reply && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reply)) {
      setReplyError(t('settings.email.sender.replyToInvalid'));
      return;
    }
    saveSender.mutate();
  }

  if (emailStatus.isPending) return <Skeleton height={160} />;
  if (!emailStatus.available) return <Navigate to="/settings" replace />;

  const address = settings?.platform_address ?? emailStatus.status?.address ?? null;
  const senderDirty = settings !== null && (senderName.trim() !== (settings.sender_name ?? '') || replyTo.trim() !== (settings.reply_to ?? ''));

  return (
    <Stack gap="md">
      {!canEdit && <Alert color="info">{t('settings.email.readOnly')}</Alert>}

      {error && (
        <Alert color="error" role="alert">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text role="bodySmall">{t('settings.email.loadError')}</Text>
            <Button size="xs" variant="outline" onClick={() => { void refetch(); }}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {/* The address: what it is for, and how to bring a mailbox's mail into the Inbox. */}
      <Card withBorder data-testid="email-address">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>{t('settings.email.address.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('settings.email.address.intro')}</Text>
          </Stack>
          {address
            ? <CopyValue value={address} label={t('settings.email.address.title')} />
            : <Text role="bodySmall" c={MUTED}>{t('settings.email.address.none')}</Text>}
          <Text role="bodySmall">{t('settings.email.address.forward')}</Text>
          {settings && (
            <dl className="rr-email__facts">
              <dt>{t('settings.email.address.sendingFrom')}</dt>
              <dd>{settings.sending_from ?? address ?? t('common.none')}</dd>
              <dt>{t('settings.email.address.replyTo')}</dt>
              <dd>{settings.effective_reply_to ?? address ?? t('common.none')}</dd>
            </dl>
          )}
        </Stack>
      </Card>

      {/* Nothing is interactive until the stored values are in hand, so the
          first paint never shows blanks over saved values. */}
      {isPending && !error && <Skeleton height={200} />}

      {settings && (
        <Card withBorder data-testid="email-sender">
          <Stack gap="md">
            <Title order={4}>{t('settings.email.sender.title')}</Title>
            <TextInput
              label={t('settings.email.sender.name')}
              description={t('settings.email.sender.nameHint')}
              value={senderName}
              maxLength={120}
              disabled={!canEdit}
              onChange={(e) => setSenderName(e.currentTarget.value)}
            />
            <TextInput
              label={t('settings.email.sender.replyTo')}
              description={t('settings.email.sender.replyToHint')}
              value={replyTo}
              type="email"
              disabled={!canEdit}
              error={replyError || undefined}
              onChange={(e) => { setReplyTo(e.currentTarget.value); setReplyError(''); }}
            />
            {canEdit && (
              <Group justify="flex-end">
                <Button loading={saveSender.isPending} disabled={!senderDirty} onClick={submitSender}>{t('action.save')}</Button>
              </Group>
            )}
          </Stack>
        </Card>
      )}

      {settings && (
        <Card withBorder data-testid="email-fallback">
          <Stack gap="md">
            <Title order={4}>{t('settings.email.fallback.title')}</Title>
            <Switch
              label={t('settings.email.fallback.label')}
              description={t('settings.email.fallback.hint')}
              checked={fallback}
              onChange={(next: boolean) => saveFallback.mutate(next)}
              disabled={!canEdit || saveFallback.isPending}
            />
            {canEdit && <Text role="caption" c={MUTED}>{t('settings.email.autosave')}</Text>}
          </Stack>
        </Card>
      )}

      {settings && <OwnDomainCard settings={settings} canEdit={canEdit} />}
    </Stack>
  );
}
